import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import { formatDay, formatDisplayDate, formatDisplayTime } from '@lensmira/i18n'
import {
  ALLOCATION_BASES,
  SHIPMENT_COST_KINDS,
  SHIPMENT_TRACKING_TRANSITIONS,
} from '@lensmira/domain'

import { Ltr, useTranslation } from '../i18n.js'
import {
  MoneyInputError,
  formatMinorForInput,
  formatWireForDisplay,
  parseMajorInput,
  parseMinorUnits,
  type WireMoney,
} from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import {
  allocationBasisWord,
  purchaseOrderStatusWord,
  shipmentCostWord,
  shipmentTrackingWord,
} from '../status-labels.js'

/**
 * Inbound shipments and what they really cost. §6.28 `IMP-001` to `IMP-006`, release gate 8.
 *
 * §6.28 opens with the reason the module exists: "**Without this, every margin figure in the
 * product is wrong** in an import-dependent market." A Pakistani optician buys frames landed at
 * Karachi, and the freight, the duty, the clearing agent and the port charges are often a fifth of
 * what the box cost. A shop pricing off the supplier invoice alone is discounting against a cost it
 * has not finished paying.
 *
 * **There is no list of shipments, and the screen says so rather than pretending.** The API has
 * `GET /shipments/:shipmentId` and no `GET /shipments`, so a shipment is reachable two ways: from
 * the answer to the create that just made one, and from a purchase order that names one
 * (`OrderView.shipmentId`). Both doors are offered here. The consequence is stated on screen as
 * well, next to the create button: a shipment with no order attached cannot be found again once
 * this screen is closed, so attaching an order is the next step and not a later one. That is a
 * missing read rather than a screen problem, and it is reported.
 *
 * **The sold quantity is asked for, line by line, because nothing in the product stores it.**
 * `IMP-003` says the landed cost of goods already sold "posts as a cost adjustment in the current
 * period, not silently backdated", which needs to know how many pieces of each delivery line have
 * sold. No table links a goods receipt line to a sale: a stock movement records a sale against a
 * stock item and a lot, never against the receipt line the pieces arrived on, and
 * `packages/domain/src/purchasing.ts` says in as many words that the `soldQuantity` definition is
 * unspecified. So the person allocating states it, the field starts blank, and nothing here
 * defaults it to zero: a zero would send every sold piece's share of the duty onto the shelf
 * instead of into this period's cost of sales, which is the one thing `IMP-003` forbids, and it
 * would do it silently.
 *
 * **The allocation basis has no default, by design** (`IMP-002`: "The basis is recorded so the
 * calculation can be audited"). The select opens on an empty choice and the screen refuses until
 * one is picked. The server refuses too, with `ALLOCATION_BASIS_REQUIRED`, and a refused write
 * stops the whole queue, so the refusal belongs here where somebody can fix it.
 *
 * **Every amount is cost price, so the whole money half can be withheld** (`ROLE-001a`'s per-user,
 * per-branch grant). `shipmentForCaller` deletes `costs` and `allocations` from the response for a
 * reader without the grant, so an absent key means withheld and an empty array means there are
 * none. The two are kept in different words: "none entered yet" is an instruction to enter one and
 * "not yours to see" is not.
 */

/** A non-goods cost on the shipment. `IMP-001`'s seven kinds, `IMP-004`'s rate and date. */
export interface ShipmentCostData {
  readonly id: string
  /** `ARC-020`: one of `SHIPMENT_COST_KINDS` off the wire, unnarrowed. */
  readonly kind: string
  readonly payee?: string
  /** `IMP-002`. Recorded on the cost and again on every allocation row it feeds. */
  readonly allocationBasis: string
  readonly amount: WireMoney
  /** The same bill in the branch's own currency, which is what the ledger is kept in. */
  readonly baseAmount: WireMoney
  readonly exchangeRate: string
  readonly exchangeRateOn: string
  /** Appendix J's lock. The cost row's own version, never the shipment's. */
  readonly version: number
}

/**
 * One cost's share of one delivery line, as the ledger has it. `IMP-002`, `IMP-003`.
 *
 * `basisQuantity` and `basisTotal` are the audit trail `IMP-002` asks for: what this line
 * contributed and what every line contributed, in whatever the basis counts (minor units of goods
 * value, grams, cubic millimetres, pieces). They are counts rather than money, so they are printed
 * as given and never put through the money formatter.
 */
export interface ShipmentAllocationData {
  readonly id: string
  readonly shipmentCostId: string
  readonly goodsReceiptLineId: string
  readonly revision: number
  readonly basis: string
  readonly basisQuantity: string
  readonly basisTotal: string
  readonly share: WireMoney
  /** The part that went onto stock still on the shelf. */
  readonly capitalised: WireMoney
  /** The part that went onto cost of sales, because those pieces had already sold. */
  readonly cogsAdjustment: WireMoney
  readonly soldQuantity: number
  /** `IMP-003`: the period the adjustment landed in. Absent when nothing had sold. */
  readonly period?: string
  readonly journalId?: string
}

export interface ShipmentData {
  readonly id: string
  readonly reference: string
  readonly branchId: string
  readonly carrier?: string
  /** `IMP-006`'s six states. `ARC-020`: a string, so a newer schema's state still renders. */
  readonly trackingState: string
  readonly expectedArrivalOn?: string
  /** When costs were first spread. An instant, shown on the branch's clock. */
  readonly costsFinalisedAt?: string
  readonly orders: readonly {
    readonly id: string
    readonly number: string
    readonly status: string
  }[]
  /**
   * `ROLE-001a`: absent means Appendix B's cost row is withheld from this reader, empty means the
   * shipment carries no cost yet. Different sentences, because the first is about the reader.
   */
  readonly costs?: readonly ShipmentCostData[]
  /** Absent for the same reason as `costs`: the two travel together through the same gate. */
  readonly allocations?: readonly ShipmentAllocationData[]
  readonly version: number
}

export interface ShipmentOrderLineData {
  readonly id: string
  readonly sku: string
  readonly description: string
  /** `INV-003`. Only an owned line takes import cost: the shop has not bought a consignment line. */
  readonly ownership: string
  /**
   * The currency the goods were bought in, with no amount beside it.
   *
   * Only the code, deliberately. It is here for one check: a cost spread by `value` needs each
   * line's goods value in the branch's own currency, and the service refuses with
   * `GOODS_VALUE_BASE_UNAVAILABLE` when a line was bought in another one. The amount is not needed
   * for that, and this screen shows no unit cost, so none is carried. Absent means the cost grant
   * is withheld and the check cannot be made at all.
   */
  readonly unitCostCurrency?: string
}

export interface ShipmentReceiptLineData {
  readonly goodsReceiptLineId: string
  readonly purchaseOrderLineId: string
  readonly quantityReceived: number
  readonly isOverReceipt: boolean
}

/** A purchase order, read for what this screen needs of it: its shipment and what arrived. */
export interface ShipmentOrderData {
  readonly id: string
  readonly number: string
  readonly status: string
  readonly version: number
  /** `IMP-001`: the shipment this order travels in, and the only way back to one. */
  readonly shipmentId?: string
  readonly lines: readonly ShipmentOrderLineData[]
  readonly receipts: readonly {
    readonly id: string
    readonly number: string
    readonly lines: readonly ShipmentReceiptLineData[]
  }[]
}

export interface ShipmentsProps {
  readonly branchId: string
  /** The branch's own currency, which is the base every cost is booked into. Never guessed. */
  readonly currency: string
  /** The branch's zone, so "costs were spread at" reads as the shop's clock and not the device's. */
  readonly timezone: string
  readonly loadShipment: (shipmentId: string) => Promise<ShipmentData>
  /**
   * Every order at this branch: the doors into a shipment, and the candidates to attach to one.
   *
   * **It has to include the closed ones.** `GET /purchase-orders` with no status leaves out
   * `closed` and `cancelled`, and a shipment whose orders have all closed is exactly the one a
   * late duty bill arrives for (`IMP-003`). Since `shipmentId` on an order is the only route back
   * to a shipment, a caller that passed only the open list would make that shipment unreachable.
   * `App.tsx` merges the two calls; a closed order stays attachable here for the same reason.
   */
  readonly loadOrders: () => Promise<readonly ShipmentOrderData[]>
  /**
   * One order in full, by id.
   *
   * The list read exists and is not enough: `GET /purchase-orders` leaves out `closed` and
   * `cancelled`, and an order whose goods all arrived and whose supplier invoice matched is closed.
   * Those are exactly the orders a shipment is allocated across, and the allocation is refused
   * unless **every** owned received line on the shipment is accounted for. So the lines come from
   * the shipment's own order ids, one read each, rather than from whatever the branch list happens
   * to still show.
   */
  readonly loadOrder: (purchaseOrderId: string) => Promise<ShipmentOrderData>
  /**
   * `landed_cost_allocate`, Appendix B's "Landed cost - allocate" row: owner, manager, accountant.
   * The read itself takes it, so a reader without it has nothing to show.
   */
  readonly canAllocate: boolean
  /**
   * `purchase_order_raise`, the row creating a shipment, attaching an order and moving tracking
   * take. Appendix B gives it to the same three roles, but the server checks the granted rows
   * rather than the role, so the two halves are two props. Offering a button whose write comes back
   * 403 stops the whole queue behind it, which is the defect `Purchasing` already shipped once.
   */
  readonly canOrder: boolean
}

/** What one delivery line looks like here: a line the import cost can land on. */
interface ReceivedLine {
  readonly goodsReceiptLineId: string
  readonly orderNumber: string
  readonly receiptNumber: string
  readonly description: string
  readonly sku: string
  readonly quantityReceived: number
  readonly isOverReceipt: boolean
  readonly unitCostCurrency?: string
}

/** The cost form, used both for a new bill and for a revision of one. */
interface CostForm {
  readonly kind: string
  readonly payee: string
  /** Empty on purpose: `IMP-002` gives no default and the server refuses an absent basis. */
  readonly basis: string
  readonly amount: string
  readonly currency: string
  readonly baseAmount: string
  readonly rate: string
  readonly rateOn: string
}

const emptyCost = (currency: string): CostForm => ({
  kind: 'freight',
  payee: '',
  basis: '',
  amount: '',
  currency,
  baseAmount: '',
  rate: '',
  rateOn: '',
})

/** `parseRate` on the server: at most eight decimal places, which a rate like 280.15 fits. */
const RATE = /^\d{1,10}(\.\d{1,8})?$/

const CURRENCY_CODE = /^[A-Z]{3}$/

/**
 * What one allocation run posted, read off the queued write's own answer.
 *
 * It is the one thing on this screen a re-read cannot recover: `rowsWritten`, the period and the
 * journal id exist only in the answer to `POST /shipments/:id/allocation`. The shipment read
 * afterwards shows the rows that now stand and not what this run moved, and "nothing moved" is a
 * real and useful answer that no later read can tell apart from "it was never run".
 *
 * Read field by field and never cast. `QueuedWrite.response` is `unknown` precisely so that nobody
 * casts it: four screens guessed a wire shape, all four typechecked, and all four crashed
 * (`PROGRESS.md` problem 109). Anything unreadable comes back absent and is not rendered, rather
 * than printing `undefined` beside a figure somebody acts on.
 */
interface AllocationRunView {
  readonly rowsWritten?: number
  readonly period?: string
  readonly posted?: {
    readonly cogs: WireMoney
    readonly inventory: WireMoney
    readonly payable: WireMoney
  }
}

const shapeOf = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined

const wireMoney = (value: unknown): WireMoney | undefined => {
  const body = shapeOf(value)
  const minor = body?.['minor']
  const currency = body?.['currency']
  // `MR-01`: minor units and a currency together, never one without the other.
  return typeof minor === 'string' && typeof currency === 'string' ? { minor, currency } : undefined
}

const runOf = (response: unknown): AllocationRunView => {
  const run = shapeOf(shapeOf(response)?.['run'])
  const rows = run?.['rowsWritten']
  const period = run?.['period']
  const posted = shapeOf(run?.['posted'])
  const cogs = wireMoney(posted?.['cogs'])
  const inventory = wireMoney(posted?.['inventory'])
  const payable = wireMoney(posted?.['payable'])
  return {
    ...(typeof rows === 'number' && Number.isInteger(rows) ? { rowsWritten: rows } : {}),
    ...(typeof period === 'string' ? { period } : {}),
    // `ROLE-001a`: `runForCaller` deletes `posted` for a reader without the cost grant, so an
    // absent one is withheld rather than a run that posted nothing.
    ...(cogs !== undefined && inventory !== undefined && payable !== undefined
      ? { posted: { cogs, inventory, payable } }
      : {}),
  }
}

/** The id of the shipment a create just made, which is the only way back into a brand new one. */
const createdIdOf = (response: unknown): string | undefined => {
  const id = shapeOf(response)?.['id']
  return typeof id === 'string' && id !== '' ? id : undefined
}

/** Which write is in flight, because what to do when it settles differs. */
type Pending = 'create' | 'allocate' | 'other'

/**
 * A cost body, or the sentence that says why there is not one.
 *
 * Discriminated rather than "the body, or an object with a problem on it": a bag of unknowns and a
 * refusal are both objects, so `'problem' in result` would narrow to neither and the message would
 * come out as `unknown`. The flag makes the compiler pick the branch.
 */
type CostAmounts =
  | { readonly ok: true; readonly body: Record<string, unknown> }
  | { readonly ok: false; readonly problem: string }

export function Shipments({
  branchId,
  currency,
  timezone,
  loadShipment,
  loadOrders,
  loadOrder,
  canAllocate,
  canOrder,
}: ShipmentsProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [openId, setOpenId] = useState<string | null>(null)
  const [shipment, setShipment] = useState<ShipmentData | null>(null)
  const [orders, setOrders] = useState<readonly ShipmentOrderData[] | null>(null)
  /** The orders on the open shipment, in full, which is where the delivery lines come from. */
  const [carried, setCarried] = useState<readonly ShipmentOrderData[]>([])
  const [reference, setReference] = useState('')
  const [carrier, setCarrier] = useState('')
  const [arrival, setArrival] = useState('')
  const [attaching, setAttaching] = useState('')
  const [cost, setCost] = useState<CostForm>(() => emptyCost(currency))
  /** The cost a revision is being written for, or null while the form is for a new one. */
  const [revising, setRevising] = useState<string | null>(null)
  const [sold, setSold] = useState<Record<string, string>>({})
  const [run, setRun] = useState<AllocationRunView | null>(null)
  const [createdWithoutId, setCreatedWithoutId] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending>('other')

  const refreshOrders = useCallback(async () => {
    try {
      setOrders(await loadOrders())
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('shipments.ordersLoadFailed'))
    }
  }, [loadOrders, t])

  const refreshShipment = useCallback(async () => {
    if (openId === null) {
      setShipment(null)
      setCarried([])
      return
    }
    try {
      const next = await loadShipment(openId)
      setShipment(next)
      // Each order in full, for its delivery lines and their ownership. The allocation has to name
      // every owned received line on the shipment, so an order read short is a refused write.
      setCarried(await Promise.all(next.orders.map((order) => loadOrder(order.id))))
      setError(null)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('shipments.loadFailed'))
    }
  }, [loadOrder, loadShipment, openId, t])

  useEffect(() => {
    void refreshOrders()
  }, [refreshOrders])

  useEffect(() => {
    void refreshShipment()
  }, [refreshShipment])

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    if (pending === 'create') {
      const created = createdIdOf(write.response)
      setReference('')
      setCarrier('')
      setArrival('')
      // No id in the answer means the shipment exists and nothing on this device knows where. Said
      // out loud, because there is no list to find it in.
      setCreatedWithoutId(created === undefined)
      if (created !== undefined) setOpenId(created)
    }
    if (pending === 'allocate') {
      // Kept and rendered: a run that wrote nothing is the answer, and no later read can say it.
      setRun(runOf(write.response))
      // The counted figures go with the run that used them. Left on the form, a second run against
      // the same shipment opens with last time's numbers already typed, and a fresh idempotency key
      // makes that a second real posting.
      setSold({})
    }
    setAttaching('')
    setCost(emptyCost(currency))
    setRevising(null)
    void refreshOrders()
    void refreshShipment()
  })

  /**
   * Every owned delivery line on the shipment, which is exactly the set the allocation must name.
   *
   * The join is receipt line to order line, because ownership lives on the order line and the
   * service filters on it. A consignment line left in comes back `UNKNOWN_LINE` and an owned line
   * left out comes back `SOLD_QUANTITY_REQUIRED`, and either one stops the queue.
   */
  const receivedLines = useMemo<readonly ReceivedLine[]>(
    () =>
      carried.flatMap((order) =>
        order.receipts.flatMap((receipt) =>
          receipt.lines.flatMap((line) => {
            const ordered = order.lines.find((l) => l.id === line.purchaseOrderLineId)
            if (ordered !== undefined && ordered.ownership !== 'owned') return []
            return [
              {
                goodsReceiptLineId: line.goodsReceiptLineId,
                orderNumber: order.number,
                receiptNumber: receipt.number,
                // A receipt line whose order line is missing from the same answer is kept rather
                // than dropped: the allocation would be refused without it, and the receipt line
                // id is a true name for it.
                description: ordered?.description ?? line.goodsReceiptLineId,
                sku: ordered?.sku ?? '',
                quantityReceived: line.quantityReceived,
                isOverReceipt: line.isOverReceipt,
                ...(ordered?.unitCostCurrency === undefined
                  ? {}
                  : { unitCostCurrency: ordered.unitCostCurrency }),
              },
            ]
          }),
        ),
      ),
    [carried],
  )

  /** `IMP-001`: an order joins one shipment, and only while it is in none. */
  const attachable = useMemo(
    () => (orders ?? []).filter((order) => order.shipmentId === undefined),
    [orders],
  )

  /** The shipments reachable from here at all, which is the orders that name one. */
  const doors = useMemo(() => {
    const found = new Map<string, string[]>()
    for (const order of orders ?? []) {
      if (order.shipmentId === undefined) continue
      found.set(order.shipmentId, [...(found.get(order.shipmentId) ?? []), order.number])
    }
    return [...found].map(([id, numbers]) => ({ id, numbers: numbers.join(', ') }))
  }, [orders])

  const queued = useCallback(
    async (path: string, label: string, body: Record<string, unknown>, kind: Pending) => {
      setError(null)
      setPending(kind)
      const write = (await enqueue({ method: 'POST', path, label, body })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue],
  )

  const create = useCallback(async () => {
    if (reference.trim() === '') {
      setError(t('shipments.referenceRequired'))
      return
    }
    setRun(null)
    setCreatedWithoutId(false)
    await queued(
      '/shipments',
      t('shipments.queueCreateLabel'),
      {
        reference: reference.trim(),
        // The counter's own branch: `IMP-003` lands the adjustment in one branch's ledger, and the
        // server checks this caller may write at it (`BR-08`).
        branchId,
        ...(carrier.trim() === '' ? {} : { carrier: carrier.trim() }),
        ...(arrival === '' ? {} : { expectedArrivalOn: arrival }),
      },
      'create',
    )
  }, [arrival, branchId, carrier, queued, reference, t])

  const attach = useCallback(async () => {
    if (shipment === null) return
    const order = attachable.find((candidate) => candidate.id === attaching)
    if (order === undefined) {
      setError(t('shipments.attachRequired'))
      return
    }
    await queued(
      `/shipments/${shipment.id}/orders`,
      t('shipments.queueAttachLabel'),
      {
        // Two locks, because the write moves two rows: `ARC-007` on the shipment and on the order.
        expectedVersion: shipment.version,
        purchaseOrderId: order.id,
        purchaseOrderVersion: order.version,
      },
      'other',
    )
  }, [attachable, attaching, queued, shipment, t])

  const advance = useCallback(
    async (to: string) => {
      if (shipment === null) return
      await queued(
        `/shipments/${shipment.id}/tracking`,
        t('shipments.queueTrackingLabel'),
        { expectedVersion: shipment.version, to },
        'other',
      )
    },
    [queued, shipment, t],
  )

  /**
   * The amounts and the rate, checked here because each refusal stops the queue.
   *
   * `IMP-004` makes the rate, its date and the booked base amount all required when the bill is not
   * in the shop's own currency, and the server makes all three **forbidden** when it is: a PKR bill
   * carrying a rate comes back `NOT_A_FOREIGN_AMOUNT`. So the form shows the three fields only for
   * a foreign currency and this refuses before either mistake is sent.
   *
   * Each amount is parsed with its **own** currency: a USD freight bill and the PKR it was booked
   * at have their own decimal places, and parsing one at the other's scale is an `MR-01` defect
   * that looks like a typo in the hundreds column.
   */
  const costAmounts = useCallback(
    (form: CostForm, billCurrency: string): CostAmounts => {
      const foreign = billCurrency !== currency
      let amountMinor: bigint
      let baseMinor: bigint | undefined
      try {
        amountMinor = parseMajorInput(form.amount, billCurrency)
        // A blank base amount on a foreign bill is refused below with the rest of `IMP-004`'s
        // three fields, in one sentence, and not here as "Amount is required" about a box that
        // is not the amount.
        baseMinor =
          foreign && form.baseAmount.trim() !== ''
            ? parseMajorInput(form.baseAmount, currency)
            : undefined
      } catch (inputError) {
        return {
          ok: false,
          problem:
            inputError instanceof MoneyInputError
              ? inputError.message
              : t('shipments.amountInvalid'),
        }
      }
      // `asPositiveMinor`: whole minor units above zero. A bill for nothing is not a bill.
      if (amountMinor <= 0n || (baseMinor !== undefined && baseMinor <= 0n)) {
        return { ok: false, problem: t('shipments.amountPositive') }
      }
      if (!foreign) return { ok: true, body: { amountMinor: amountMinor.toString() } }
      if (baseMinor === undefined || form.rate.trim() === '' || form.rateOn === '') {
        return { ok: false, problem: t('shipments.rateRequired', { currency }) }
      }
      if (!RATE.test(form.rate.trim()) || Number(form.rate.trim()) <= 0) {
        return { ok: false, problem: t('shipments.rateInvalid') }
      }
      return {
        ok: true,
        body: {
          amountMinor: amountMinor.toString(),
          baseAmountMinor: baseMinor.toString(),
          exchangeRate: form.rate.trim(),
          exchangeRateOn: form.rateOn,
        },
      }
    },
    [currency, t],
  )

  const addCost = useCallback(async () => {
    if (shipment === null) return
    if (cost.basis === '') {
      // `IMP-002` names no default and the server refuses an absent basis, so the screen asks.
      setError(t('shipments.basisRequired'))
      return
    }
    if (!CURRENCY_CODE.test(cost.currency)) {
      setError(t('shipments.currencyInvalid'))
      return
    }
    const amounts = costAmounts(cost, cost.currency)
    if (!amounts.ok) {
      setError(amounts.problem)
      return
    }
    await queued(
      `/shipments/${shipment.id}/costs`,
      t('shipments.queueCostLabel'),
      {
        kind: cost.kind,
        allocationBasis: cost.basis,
        ...(cost.payee.trim() === '' ? {} : { payee: cost.payee.trim() }),
        // Sent even when it is the branch's own, so the record says what was entered rather than
        // what was assumed on its behalf.
        currency: cost.currency,
        ...amounts.body,
      },
      // No `expectedVersion`: `AddCost` has no such field and adding a cost does not move the
      // shipment row. Sending one would change the body the idempotency key is hashed with.
      'other',
    )
  }, [cost, costAmounts, queued, shipment, t])

  const revise = useCallback(
    async (existing: ShipmentCostData) => {
      if (shipment === null) return
      const amounts = costAmounts(cost, existing.amount.currency)
      if (!amounts.ok) {
        setError(amounts.problem)
        return
      }
      await queued(
        `/shipments/${shipment.id}/costs/${existing.id}/revision`,
        t('shipments.queueReviseLabel'),
        {
          // The cost row's own version. `reviseCost` locks the shipment and never bumps it, so the
          // shipment's version here would be a conflict against a row nobody had touched.
          expectedVersion: existing.version,
          ...amounts.body,
        },
        'other',
      )
    },
    [cost, costAmounts, queued, shipment, t],
  )

  const startRevision = (existing: ShipmentCostData): void => {
    setError(null)
    setRevising(existing.id)
    setCost({
      kind: existing.kind,
      payee: existing.payee ?? '',
      // The basis cannot change: an allocation row is held to its cost's basis by a composite key.
      basis: existing.allocationBasis,
      // Bare digits, which is what the money input takes back. The grouped display form comes back
      // out of `parseMajorInput` as "Not a valid amount".
      amount: formatMinorForInput(parseMinorUnits(existing.amount.minor), existing.amount.currency),
      currency: existing.amount.currency,
      baseAmount: formatMinorForInput(
        parseMinorUnits(existing.baseAmount.minor),
        existing.baseAmount.currency,
      ),
      rate: existing.exchangeRate,
      rateOn: existing.exchangeRateOn,
    })
  }

  const allocate = useCallback(async () => {
    if (shipment === null) return
    const lines: { goodsReceiptLineId: string; soldQuantity: number }[] = []
    for (const line of receivedLines) {
      const typed = (sold[line.goodsReceiptLineId] ?? '').trim()
      if (typed === '') {
        // A line left out is `SOLD_QUANTITY_REQUIRED`, and a refused write stops everything queued
        // behind it. Nothing here fills the blank with a zero: see this file's header.
        setError(t('shipments.soldRequired'))
        return
      }
      const quantity = Number(typed)
      if (!Number.isInteger(quantity) || quantity < 0) {
        setError(t('shipments.soldInvalid'))
        return
      }
      if (quantity > line.quantityReceived) {
        // `SOLD_EXCEEDS_RECEIVED` in the domain, and the screen already knows the answer.
        setError(t('shipments.soldTooMany'))
        return
      }
      lines.push({ goodsReceiptLineId: line.goodsReceiptLineId, soldQuantity: quantity })
    }
    const spreadByValue = (shipment.costs ?? []).some(
      (entry) =>
        entry.allocationBasis === 'value' && parseMinorUnits(entry.baseAmount.minor) !== 0n,
    )
    const bought = receivedLines.find(
      (line) => line.unitCostCurrency !== undefined && line.unitCostCurrency !== currency,
    )
    if (spreadByValue && bought !== undefined) {
      // `GOODS_VALUE_BASE_UNAVAILABLE`: `IMP-004` records a foreign purchase's booked base amount
      // on the invoice as a whole and nowhere per line, so a line bought in another currency has no
      // goods value in base to spread by. Weight, volume and unit count still work, which is the
      // part worth saying.
      setError(
        t('shipments.valueBasisImpossible', {
          bought: bought.unitCostCurrency ?? '',
          base: currency,
        }),
      )
      return
    }
    setRun(null)
    await queued(
      `/shipments/${shipment.id}/allocation`,
      t('shipments.queueAllocateLabel'),
      { expectedVersion: shipment.version, lines },
      'allocate',
    )
  }, [currency, queued, receivedLines, shipment, sold, t])

  const when = (iso: string): string => {
    const instant = Date.parse(iso)
    if (!Number.isFinite(instant)) return iso
    // The catalogue's own formatters, never `Intl` here: `LOC-004`, `D-03` and `TY-03` keep the
    // counter on Western digits in every locale, and `packages/i18n/src/format.ts` is the one place
    // that decides what a date looks like.
    return `${formatDisplayDate(instant, timezone, locale)} ${formatDisplayTime(instant, timezone, locale)}`
  }

  /**
   * `IMP-006`'s edges, from the domain's own table, so a state this build does not know offers
   * nothing at all. Widened to a string key on purpose: `ARC-020` keeps the state off the wire
   * unnarrowed, and a lookup that had to be cast to the enum first would have to decide what an
   * unknown state is before it could ask.
   */
  const edges = SHIPMENT_TRACKING_TRANSITIONS as Readonly<Record<string, readonly string[]>>
  const nextStates = (state: string): readonly string[] => edges[state] ?? []

  const line = statusLine(t, snapshot, writeId, t('queue.subject.shipment'))
  const costs = shipment?.costs
  const allocations = shipment?.allocations

  return (
    <section className="lm-screen" aria-labelledby="shipments-heading">
      <h1 id="shipments-heading" className="lm-screen__heading">
        {t('shipments.heading')}
      </h1>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {shipment === null ? (
        <>
          {/* The missing read, said out loud rather than worked around in silence. */}
          <p className="lm-hint">{t('shipments.noList')}</p>

          {createdWithoutId ? (
            <p className="lm-hint" role="alert">
              {t('shipments.createdWithoutId')}
            </p>
          ) : null}

          <h2 className="lm-screen__subheading">{t('shipments.openHeading')}</h2>
          {orders === null ? (
            <p className="lm-hint">{t('shipments.loadingOrders')}</p>
          ) : doors.length === 0 ? (
            <p className="lm-empty">{t('shipments.noneToOpen')}</p>
          ) : (
            <ul className="lm-shipment-doors">
              {doors.map((door) => (
                <li key={door.id} className="lm-shipment-door">
                  {/* The orders are what names it: there is no reference until it is read. */}
                  <span className="lm-shipment-door__orders" dir="ltr">
                    <Ltr>{door.numbers}</Ltr>
                  </span>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      setError(null)
                      setRun(null)
                      setOpenId(door.id)
                    }}
                  >
                    {t('shipments.open')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {canOrder ? (
            <>
              <h2 className="lm-screen__subheading">{t('shipments.newHeading')}</h2>
              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('shipments.referenceLabel')}</span>
                <input
                  className="lm-input"
                  autoComplete="off"
                  value={reference}
                  onChange={(e) => {
                    setReference(e.currentTarget.value)
                  }}
                />
              </label>
              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('shipments.carrierLabel')}</span>
                <input
                  className="lm-input"
                  autoComplete="off"
                  value={carrier}
                  onChange={(e) => {
                    setCarrier(e.currentTarget.value)
                  }}
                />
              </label>
              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('shipments.arrivalLabel')}</span>
                <input
                  className="lm-input"
                  type="date"
                  value={arrival}
                  onChange={(e) => {
                    setArrival(e.currentTarget.value)
                  }}
                />
              </label>
              {/* The consequence of there being no list, beside the button that makes one. */}
              <p className="lm-hint">{t('shipments.attachFirst')}</p>
              <button
                type="button"
                className="lm-button lm-button--primary"
                onClick={() => void create()}
              >
                {t('shipments.create')}
              </button>
            </>
          ) : (
            <p className="lm-hint">{t('shipments.orderingElsewhere')}</p>
          )}
        </>
      ) : (
        <>
          <div className="lm-shipment">
            <span className="lm-shipment__reference" dir="ltr">
              <Ltr>{shipment.reference}</Ltr>
            </span>
            {/* `ARC-020`: a tracking state this build has no word for prints as itself. */}
            <span className="lm-shipment__state">
              {shipmentTrackingWord(t, shipment.trackingState)}
            </span>
            {shipment.carrier === undefined ? null : (
              <span className="lm-shipment__carrier">
                {t('shipments.carriedBy', { carrier: shipment.carrier })}
              </span>
            )}
            {shipment.expectedArrivalOn === undefined ? (
              <span className="lm-shipment__due">{t('shipments.noArrivalDate')}</span>
            ) : (
              <span className="lm-shipment__due">
                {sentence(t, 'shipments.due', { date: <Ltr>{formatDay(shipment.expectedArrivalOn, timezone, locale)}</Ltr> })}
              </span>
            )}
            {shipment.costsFinalisedAt === undefined ? null : (
              <span className="lm-shipment__finalised">
                {sentence(t, 'shipments.firstSpread', {
                  when: <Ltr>{when(shipment.costsFinalisedAt)}</Ltr>,
                })}
              </span>
            )}
            <button
              type="button"
              className="lm-button lm-button--secondary"
              onClick={() => {
                setError(null)
                setRun(null)
                setSold({})
                setOpenId(null)
              }}
            >
              {t('shipments.leave')}
            </button>
          </div>

          {canOrder ? (
            nextStates(shipment.trackingState).length === 0 ? (
              <p className="lm-hint">
                {/* Either it has arrived, or this build does not know the state it is in. Both are
                    true, and neither offers a button the server would refuse. */}
                {edges[shipment.trackingState] === undefined
                  ? t('shipments.trackingUnknown', { state: shipment.trackingState })
                  : t('shipments.trackingDone')}
              </p>
            ) : (
              <div className="lm-shipment__tracking">
                {nextStates(shipment.trackingState).map((next) => (
                  <button
                    key={next}
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => void advance(next)}
                  >
                    {/* A translated noun in a whole sentence's hole, which is how `queue.statusLine`
                        carries its subject. Never two translated sentences joined (Appendix S). */}
                    {t('shipments.moveTo', { state: shipmentTrackingWord(t, next) })}
                  </button>
                ))}
              </div>
            )
          ) : null}

          <h2 className="lm-screen__subheading">{t('shipments.ordersHeading')}</h2>
          {shipment.orders.length === 0 ? (
            <p className="lm-empty">{t('shipments.noOrders')}</p>
          ) : (
            <ul className="lm-shipment-order-list">
              {shipment.orders.map((order) => (
                <li key={order.id} className="lm-shipment-order">
                  <span className="lm-shipment-order__number" dir="ltr">
                    <Ltr>{order.number}</Ltr>
                  </span>
                  <span className="lm-shipment-order__status">
                    {purchaseOrderStatusWord(t, order.status)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {canOrder ? (
            attachable.length === 0 ? (
              <p className="lm-hint">{t('shipments.attachNone')}</p>
            ) : (
              <>
                <label className="lm-field lm-field--inline">
                  <span className="lm-field__label">{t('shipments.attachLabel')}</span>
                  <select
                    className="lm-input"
                    value={attaching}
                    onChange={(e) => {
                      setAttaching(e.currentTarget.value)
                    }}
                  >
                    <option value="">{t('shipments.attachChoose')}</option>
                    {/* Only an order in no shipment: a second one comes back
                        `ORDER_ALREADY_ON_SHIPMENT`, and the branch is already this one. */}
                    {attachable.map((order) => (
                      <option key={order.id} value={order.id}>
                        {order.number}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => void attach()}
                >
                  {t('shipments.attach')}
                </button>
              </>
            )
          ) : null}

          {canAllocate ? (
            <>
              <h2 className="lm-screen__subheading">{t('shipments.costsHeading')}</h2>
              {costs === undefined ? (
                // `ROLE-001a`: the key is gone, which is a fact about the reader and not the box.
                <p className="lm-hint">{t('shipments.costsWithheld')}</p>
              ) : costs.length === 0 ? (
                <p className="lm-empty">{t('shipments.noCosts')}</p>
              ) : (
                <ul className="lm-shipment-cost-list">
                  {costs.map((entry) => (
                    <li key={entry.id} className="lm-shipment-cost">
                      <span className="lm-shipment-cost__kind">
                        {shipmentCostWord(t, entry.kind)}
                      </span>
                      <span className="lm-shipment-cost__amount" dir="ltr">
                        {formatWireForDisplay(entry.amount.minor, entry.amount.currency)}
                      </span>
                      <span className="lm-shipment-cost__basis">
                        {t('shipments.spreadBy', {
                          basis: allocationBasisWord(t, entry.allocationBasis),
                        })}
                      </span>
                      {entry.payee === undefined ? null : (
                        <span className="lm-shipment-cost__payee">
                          {t('shipments.paidTo', { payee: entry.payee })}
                        </span>
                      )}
                      {entry.amount.currency === entry.baseAmount.currency ? null : (
                        // `IMP-004`: the rate and its date travel with the bill, because the books
                        // are kept in the branch's currency and the bank's figure is the record.
                        <span className="lm-shipment-cost__rate">
                          {sentence(t, 'shipments.bookedAt', {
                            amount: (
                              <Ltr>
                                {formatWireForDisplay(
                                  entry.baseAmount.minor,
                                  entry.baseAmount.currency,
                                )}
                              </Ltr>
                            ),
                            rate: <Ltr>{entry.exchangeRate}</Ltr>,
                            date: <Ltr>{formatDay(entry.exchangeRateOn, timezone, locale)}</Ltr>,
                          })}
                        </span>
                      )}
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        aria-expanded={revising === entry.id}
                        onClick={() => {
                          if (revising === entry.id) {
                            setRevising(null)
                            setCost(emptyCost(currency))
                            setError(null)
                            return
                          }
                          startRevision(entry)
                        }}
                      >
                        {t('shipments.revise')}
                      </button>
                      {revising === entry.id ? (
                        <div className="lm-shipment-cost__revision">
                          {/* The basis is fixed: an allocation row is held to its cost's basis by a
                              composite key, so a changed basis could not be stored. */}
                          <p className="lm-hint">
                            {t('shipments.basisFixed', {
                              basis: allocationBasisWord(t, entry.allocationBasis),
                            })}
                          </p>
                          <label className="lm-field lm-field--inline">
                            <span className="lm-field__label">
                              {t('shipments.amountLabel', { currency: entry.amount.currency })}
                            </span>
                            <input
                              className="lm-input"
                              inputMode="decimal"
                              autoComplete="off"
                              value={cost.amount}
                              onChange={(e) => {
                                const value = e.currentTarget.value
                                setCost((previous) => ({ ...previous, amount: value }))
                              }}
                            />
                          </label>
                          {entry.amount.currency === currency ? (
                            <p className="lm-hint">{t('shipments.sameCurrency', { currency })}</p>
                          ) : (
                            <>
                              <label className="lm-field lm-field--inline">
                                <span className="lm-field__label">
                                  {t('shipments.baseAmountLabel', { currency })}
                                </span>
                                <input
                                  className="lm-input"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  value={cost.baseAmount}
                                  onChange={(e) => {
                                    const value = e.currentTarget.value
                                    setCost((previous) => ({ ...previous, baseAmount: value }))
                                  }}
                                />
                              </label>
                              <label className="lm-field lm-field--inline">
                                <span className="lm-field__label">{t('shipments.rateLabel')}</span>
                                <input
                                  className="lm-input"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  value={cost.rate}
                                  onChange={(e) => {
                                    const value = e.currentTarget.value
                                    setCost((previous) => ({ ...previous, rate: value }))
                                  }}
                                />
                              </label>
                              <label className="lm-field lm-field--inline">
                                <span className="lm-field__label">
                                  {t('shipments.rateOnLabel')}
                                </span>
                                <input
                                  className="lm-input"
                                  type="date"
                                  value={cost.rateOn}
                                  onChange={(e) => {
                                    const value = e.currentTarget.value
                                    setCost((previous) => ({ ...previous, rateOn: value }))
                                  }}
                                />
                              </label>
                              <p className="lm-hint">{t('shipments.rateHint', { currency })}</p>
                            </>
                          )}
                          <button
                            type="button"
                            className="lm-button lm-button--primary"
                            onClick={() => void revise(entry)}
                          >
                            {t('shipments.saveRevision')}
                          </button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}

              {/**
               * Not while a revision is open, and not at all without the cost grant.
               *
               * One form for both jobs, as `Promotions` does: the amount, the currency and the rate
               * are the same four fields either way, and two of them on screen at once would be two
               * inputs bound to one piece of state, each labelled "Amount, in PKR". Typing in one
               * would silently change the other.
               */}
              {costs === undefined || revising !== null ? null : (
                <>
                  <h2 className="lm-screen__subheading">{t('shipments.newCostHeading')}</h2>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('shipments.kindLabel')}</span>
                    <select
                      className="lm-input"
                      value={cost.kind}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        setCost((previous) => ({ ...previous, kind: value }))
                      }}
                    >
                      {/* `IMP-001`'s seven, from the domain's own list rather than a copy of it. */}
                      {SHIPMENT_COST_KINDS.map((kind) => (
                        <option key={kind} value={kind}>
                          {shipmentCostWord(t, kind)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('shipments.payeeLabel')}</span>
                    <input
                      className="lm-input"
                      autoComplete="off"
                      value={cost.payee}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        setCost((previous) => ({ ...previous, payee: value }))
                      }}
                    />
                  </label>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('shipments.basisLabel')}</span>
                    <select
                      className="lm-input"
                      value={cost.basis}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        setCost((previous) => ({ ...previous, basis: value }))
                      }}
                    >
                      {/* Empty first and no default: `IMP-002` records the basis so the figure can
                          be audited later, and a basis nobody chose is not auditable. */}
                      <option value="">{t('shipments.basisChoose')}</option>
                      {ALLOCATION_BASES.map((basis) => (
                        <option key={basis} value={basis}>
                          {allocationBasisWord(t, basis)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {/* What this screen cannot check for you, said rather than discovered. */}
                  <p className="lm-hint">{t('shipments.basisHint')}</p>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('shipments.currencyLabel')}</span>
                    <input
                      className="lm-input"
                      autoComplete="off"
                      value={cost.currency}
                      onChange={(e) => {
                        const value = e.currentTarget.value.toUpperCase()
                        setCost((previous) => ({ ...previous, currency: value }))
                      }}
                    />
                  </label>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('shipments.amountLabel', { currency: cost.currency })}
                    </span>
                    <input
                      className="lm-input"
                      inputMode="decimal"
                      autoComplete="off"
                      value={cost.amount}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        setCost((previous) => ({ ...previous, amount: value }))
                      }}
                    />
                  </label>
                  {cost.currency === currency ? (
                    <p className="lm-hint">{t('shipments.sameCurrency', { currency })}</p>
                  ) : (
                    <>
                      <label className="lm-field lm-field--inline">
                        <span className="lm-field__label">
                          {t('shipments.baseAmountLabel', { currency })}
                        </span>
                        <input
                          className="lm-input"
                          inputMode="decimal"
                          autoComplete="off"
                          value={cost.baseAmount}
                          onChange={(e) => {
                            const value = e.currentTarget.value
                            setCost((previous) => ({ ...previous, baseAmount: value }))
                          }}
                        />
                      </label>
                      <label className="lm-field lm-field--inline">
                        <span className="lm-field__label">{t('shipments.rateLabel')}</span>
                        <input
                          className="lm-input"
                          inputMode="decimal"
                          autoComplete="off"
                          value={cost.rate}
                          onChange={(e) => {
                            const value = e.currentTarget.value
                            setCost((previous) => ({ ...previous, rate: value }))
                          }}
                        />
                      </label>
                      <label className="lm-field lm-field--inline">
                        <span className="lm-field__label">{t('shipments.rateOnLabel')}</span>
                        <input
                          className="lm-input"
                          type="date"
                          value={cost.rateOn}
                          onChange={(e) => {
                            const value = e.currentTarget.value
                            setCost((previous) => ({ ...previous, rateOn: value }))
                          }}
                        />
                      </label>
                      <p className="lm-hint">{t('shipments.rateHint', { currency })}</p>
                    </>
                  )}
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    onClick={() => void addCost()}
                  >
                    {t('shipments.addCost')}
                  </button>
                </>
              )}

              <h2 className="lm-screen__subheading">{t('shipments.allocationHeading')}</h2>
              {allocations === undefined ? (
                <p className="lm-hint">{t('shipments.allocationWithheld')}</p>
              ) : allocations.length === 0 ? (
                <p className="lm-empty">{t('shipments.noAllocation')}</p>
              ) : (
                <ul className="lm-allocation-list">
                  {allocations.map((row) => (
                    <li key={row.id} className="lm-allocation">
                      <span className="lm-allocation__share" dir="ltr">
                        {formatWireForDisplay(row.share.minor, row.share.currency)}
                      </span>
                      <span className="lm-allocation__split">
                        {sentence(t, 'shipments.split', {
                          onStock: (
                            <Ltr>
                              {formatWireForDisplay(
                                row.capitalised.minor,
                                row.capitalised.currency,
                              )}
                            </Ltr>
                          ),
                          onSold: (
                            <Ltr>
                              {formatWireForDisplay(
                                row.cogsAdjustment.minor,
                                row.cogsAdjustment.currency,
                              )}
                            </Ltr>
                          ),
                        })}
                      </span>
                      {/* `IMP-002`'s audit trail: this line's figure out of every line's, in
                          whatever the basis counts. Not money, so not formatted as money. */}
                      <span className="lm-allocation__basis">
                        {sentence(t, 'shipments.basisShare', {
                          basis: allocationBasisWord(t, row.basis),
                          quantity: <Ltr>{row.basisQuantity}</Ltr>,
                          total: <Ltr>{row.basisTotal}</Ltr>,
                        })}
                      </span>
                      <span className="lm-allocation__sold">
                        {t('shipments.soldOf', { count: row.soldQuantity })}
                      </span>
                      {row.period === undefined ? null : (
                        <span className="lm-allocation__period">
                          {sentence(t, 'shipments.inPeriod', { period: <Ltr>{row.period}</Ltr> })}
                        </span>
                      )}
                      <span className="lm-allocation__revision">
                        {t('shipments.workingNumber', { revision: row.revision })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {run === null ? null : (
                <div className="lm-allocation-run" role="status">
                  {run.rowsWritten === 0 ? (
                    <p className="lm-hint">{t('shipments.runNothing')}</p>
                  ) : (
                    <p className="lm-hint">
                      {run.rowsWritten === undefined || run.period === undefined
                        ? t('shipments.runDone')
                        : sentence(t, 'shipments.runWrote', {
                            count: run.rowsWritten,
                            period: <Ltr>{run.period}</Ltr>,
                          })}
                    </p>
                  )}
                  {run.posted === undefined ? (
                    run.rowsWritten === 0 ? null : (
                      <p className="lm-hint">{t('shipments.runPostedWithheld')}</p>
                    )
                  ) : (
                    <p className="lm-hint">
                      {sentence(t, 'shipments.runPosted', {
                        cogs: (
                          <Ltr>
                            {formatWireForDisplay(run.posted.cogs.minor, run.posted.cogs.currency)}
                          </Ltr>
                        ),
                        inventory: (
                          <Ltr>
                            {formatWireForDisplay(
                              run.posted.inventory.minor,
                              run.posted.inventory.currency,
                            )}
                          </Ltr>
                        ),
                        payable: (
                          <Ltr>
                            {formatWireForDisplay(
                              run.posted.payable.minor,
                              run.posted.payable.currency,
                            )}
                          </Ltr>
                        ),
                      })}
                    </p>
                  )}
                </div>
              )}

              <h2 className="lm-screen__subheading">{t('shipments.allocateHeading')}</h2>
              {receivedLines.length === 0 ? (
                // Nothing has arrived, so there is nothing for the duty to land on. The server
                // answers `NO_ELIGIBLE_LINES` once a cost exists.
                <p className="lm-empty">{t('shipments.allocateNoLines')}</p>
              ) : costs !== undefined && costs.length === 0 ? (
                <p className="lm-empty">{t('shipments.allocateNoCosts')}</p>
              ) : (
                <>
                  {/* The whole reason this form exists, in terms of what it decides. */}
                  <p className="lm-hint">{t('shipments.soldHint')}</p>
                  <table className="lm-line-table">
                    <caption className="lm-rx-grid__caption">
                      {t('shipments.linesCaption', { reference: shipment.reference })}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('shipments.col.item')}</th>
                        <th scope="col">{t('shipments.col.arrived')}</th>
                        <th scope="col">{t('shipments.col.sold')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receivedLines.map((row) => (
                        <tr key={row.goodsReceiptLineId}>
                          <th scope="row" className="lm-line-table__description">
                            {row.description}
                            <span className="lm-line-table__meta" dir="ltr">
                              <Ltr>{`${row.sku} ${row.orderNumber} ${row.receiptNumber}`}</Ltr>
                            </span>
                            {row.isOverReceipt ? (
                              <span className="lm-line-table__meta">
                                {t('shipments.overReceipt')}
                              </span>
                            ) : null}
                          </th>
                          <td className="lm-line-table__num" dir="ltr">
                            {row.quantityReceived}
                          </td>
                          <td className="lm-line-table__num">
                            <label>
                              <span className="lm-visually-hidden">
                                {t('shipments.soldLabel', { description: row.description })}
                              </span>
                              <input
                                className="lm-input"
                                inputMode="numeric"
                                autoComplete="off"
                                value={sold[row.goodsReceiptLineId] ?? ''}
                                onChange={(e) => {
                                  const value = e.currentTarget.value
                                  setSold((previous) => ({
                                    ...previous,
                                    [row.goodsReceiptLineId]: value,
                                  }))
                                }}
                              />
                            </label>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* `INV-003`: a consignment line is saleable stock the shop has not bought, so
                      none of the shop's import cost lands on it and the service leaves it out. */}
                  <p className="lm-hint">{t('shipments.consignmentLeftOut')}</p>
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    onClick={() => void allocate()}
                  >
                    {t('shipments.allocate')}
                  </button>
                </>
              )}
            </>
          ) : null}
        </>
      )}
    </section>
  )
}
